<script lang="ts">
import { defineComponent, ref, watch, computed } from "vue";
import { useDisplay } from "@/composables/useDisplay";
import DialogContentLayout from "@/components/common/DialogContentLayout.vue";

export default defineComponent({
  name: "DialogLayout",
  components: {
    DialogContentLayout,
  },
  props: {
    value: Boolean,
    title: String,
    subtitle: String,
    persistent: Boolean,
    preventDefaultHeaderClose: { type: Boolean, default: false },
    overrideFullscreen: { type: Boolean, default: false },
    headerActionRightText: String,
    headerActionLeftText: String,
  },
  setup(props, { emit, slots }) {
    const { xs } = useDisplay();
    const fullscreen = computed(() => {
      // Only fullscreen on mobile if NOT forced to windowed
      return !props.overrideFullscreen && xs.value;
    });
    const isDialogOpen = ref(props.value);

    watch(
      () => props.value,
      (newValue) => {
        isDialogOpen.value = newValue;
      }
    );

    watch(
      () => isDialogOpen.value,
      (newValue) => {
        if (newValue !== props.value) {
          emit("input", newValue);
        }
      }
    );

    const handleClose = () => {
      isDialogOpen.value = false;
      emit("input", false);
    };

    const activatorOn = {
      click: () => {
        isDialogOpen.value = true;
      },
    };
    const activatorAttrs = {};

    const handleHeaderActionRight = () => {
      if (!props.preventDefaultHeaderClose) {
        handleClose();
      } else {
        emit("click:header-action-right");
      }
    };

    const handleHeaderActionLeft = () => {
      emit("click:header-action-left");
    };

    return {
      fullscreen,
      isDialogOpen,
      handleClose,
      activatorOn,
      activatorAttrs,
      handleHeaderActionRight,
      handleHeaderActionLeft,
      slots,
    };
  },
});
</script>

<!-- You can pass in either a DialogContentLayout as "#contentLayout" or a pass in the content
and footer straight into the DialogLayout component, in most cases you will just pass in
the content and footer, but being able to pass in DialogContentLayout is useful for
cases like the multi-stage steppers where you need to pass in the content and footer together
but are using the same DialogContentLayout in both cases to keep styling consistent-->

<template>
  <div>
    <!-- Activator slot support -->
    <template v-if="$scopedSlots.activator">
      <slot name="activator" :on="activatorOn" :attrs="activatorAttrs" />
    </template>
    <v-dialog
      v-model="isDialogOpen"
      :fullscreen="fullscreen"
      width="500px"
      :persistent="persistent"
    >
      <div
        :class="[
          'd-flex flex-column h-full padding-a-8',
          { 'max-h-nine-tenths-vh': $vuetify.breakpoint.smAndUp },
        ]"
      >
        <div class="dialog-header d-flex w-full justify-between align-center margin-b-2">
          <v-btn
            v-if="headerActionLeftText"
            id="dialog-layout-header-action-left-button"
            color="gray"
            plain
            x-small
            class="font-14 font-book min-w-0 padding-x-2 margin-l-n2 margin-y-n4 padding-b-1"
            @click="handleHeaderActionLeft"
          >
            {{ headerActionLeftText }}
          </v-btn>
          <v-spacer />
          <v-btn
            color="gray"
            id="dialog-layout-header-action-right-button"
            plain
            x-small
            class="font-14 font-book min-w-0 padding-x-2 margin-r-n2 margin-y-n4 padding-b-1"
            @click="handleHeaderActionRight"
          >
            <template v-if="headerActionRightText">{{
              headerActionRightText
            }}</template>
            <CUIcon v-else view-box="0 0 24 24" width="20" height="20"
              >close</CUIcon
            >
          </v-btn>
        </div>

        <slot name="contentLayout">
          <DialogContentLayout :title="title" :subtitle="subtitle">
            <slot />
            <template #footer>
              <slot name="footer" />
            </template>
          </DialogContentLayout>
        </slot>
      </div>
    </v-dialog>
  </div>
</template>

<style scoped>
.dialog-header {
  position: sticky;
  top: 0;
  background: white;
  z-index: 2;
  padding-bottom: 8px;
}
.dialog-footer {
  position: sticky;
  bottom: 0;
  background: white;
  z-index: 2;
  display: flex;
  flex-direction: column;
  padding-top: 8px;
}
.dialog-content {
  min-height: 0;
}
</style>
